import { detectAnomalies } from './anomaly';

// One normalised telemetry shape for the stage + HUD, from either source:
//   real — { metrics: { slug: [{ ts, req, s4xx, s5xx, bytes, p50, p95, uniqIps, src: {fromSlug: n} }] (minute or hour rows) },
//            planeCalls: { slug: [{ ts, n }] }, usageDays: { slug: { 'YYYY-MM-DD': counters } },
//            anomalies?: [...] (server-detected; merged with client detection) }
//   sim  — the same shape produced by simulate.js
// Output: { source, window, series, flows, planeCalls, anomalies, activity, totals, live }
// The scene and HUD read only this; flipping Simulate changes the source,
// never the renderer.
export const WINDOWS = {
  '1h': { key: '1h', ms: 3600e3, step: 60e3, label: '1h' },
  '24h': { key: '24h', ms: 86400e3, step: 5 * 60e3, label: '24h' },
  '7d': { key: '7d', ms: 7 * 86400e3, step: 3600e3, label: '7d' },
};
const METRICS = ['req', 's4xx', 's5xx', 'bytes', 'tokens', 'convos'];

export function emptyTelemetry(windowKey = '24h') {
  const w = WINDOWS[windowKey] || WINDOWS['24h'];
  return { source: 'none', window: { ...w, n: Math.round(w.ms / w.step) }, series: {}, flows: [], planeCalls: {}, anomalies: [], activity: { hourly: new Array(24).fill(0), peakHour: null, label: 'no activity data' }, totals: { req: 0, s5xx: 0, p95: null, tokens: 0, active: 0 }, live: {}, usageDays: {}, runtime: {}, scenario: null };
}

// Bucket raw rows (any resolution) into the window's step. Rates sum,
// latencies take the max-weighted mean (p95 ≈ max of the bucket).
function bucketRows(rows, w, now) {
  const n = Math.round(w.ms / w.step);
  const start = now - n * w.step;
  const ts = []; const out = {}; for (const m of METRICS) out[m] = new Array(n).fill(0);
  out.p95 = new Array(n).fill(0); out.p50 = new Array(n).fill(0);
  const cnt = new Array(n).fill(0);
  for (let i = 0; i < n; i += 1) ts.push(start + i * w.step);
  for (const r of rows || []) {
    const i = Math.floor((r.ts - start) / w.step);
    if (i < 0 || i >= n) continue;
    for (const m of METRICS) out[m][i] += Number(r[m]) || 0;
    out.p95[i] = Math.max(out.p95[i], Number(r.p95) || 0);
    out.p50[i] += Number(r.p50) || 0; cnt[i] += 1;
  }
  for (let i = 0; i < n; i += 1) if (cnt[i]) out.p50[i] /= cnt[i];
  // Per-minute rates so the engine's floors mean the same at every step.
  const perMin = w.step / 60e3;
  if (perMin !== 1) for (const m of ['req', 's4xx', 's5xx']) out[m] = out[m].map((v) => v / perMin);
  return { ts, ...out };
}

export function deriveTelemetry(src, windowKey = '24h', now = Date.now(), thresholds) {
  if (!src || !src.metrics) return emptyTelemetry(windowKey);
  const w = WINDOWS[windowKey] || WINDOWS['24h'];
  const series = {};
  for (const [slug, rows] of Object.entries(src.metrics)) series[slug] = bucketRows(rows, w, now);

  // Flows: aggregate src counters over the window into edges with a per-minute rate.
  const edge = {};
  for (const [slug, rows] of Object.entries(src.metrics)) {
    for (const r of rows || []) {
      if (r.ts < now - w.ms) continue;
      for (const [from, c] of Object.entries(r.src || {})) {
        const k = `${from}>${slug}`;
        edge[k] = edge[k] || { from, to: slug, n: 0, kind: 'http' };
        edge[k].n += Number(c) || 0;
      }
    }
  }
  for (const f of src.flows || []) { const k = `${f.from}>${f.to}`; edge[k] = edge[k] || { from: f.from, to: f.to, n: 0, kind: f.kind || 'http' }; edge[k].n += Number(f.n) || 0; }
  const minutes = w.ms / 60e3;
  const flows = Object.values(edge).map((e) => ({ ...e, ratePerMin: e.n / minutes })).filter((e) => e.ratePerMin > 0).sort((a, b) => b.ratePerMin - a.ratePerMin).slice(0, 40);

  // Control-plane calls per slug (license / usage / agent routes) → rate.
  const planeCalls = {};
  for (const [slug, rows] of Object.entries(src.planeCalls || {})) {
    let n = 0; let last = 0;
    for (const r of rows || []) if (r.ts >= now - w.ms) { n += Number(r.n) || 0; last = Math.max(last, r.ts); }
    planeCalls[slug] = { ratePerMin: n / minutes, lastAt: last || null };
  }

  const detected = detectAnomalies(series, { flows, now, step: w.step, thresholds, usageDays: src.usageDays || {} });
  // Runtime (inside-out) signals from the kit's hourly report: stale claims
  // and an old backlog are anomalies in their own right — the edge cannot
  // see a stuck worker, the queue can.
  const runtime = src.runtime || {};
  for (const [slug, rt] of Object.entries(runtime)) {
    if (!rt || (rt.at && now - rt.at > 3 * 3600e3)) continue;
    const t = rt.totals || {};
    if (t.stale > 0) detected.push({ id: `${slug}:queue:stale:${rt.at || now}`, slug, metric: 'queue', kind: 'stuck', value: t.stale, baseline: 0, z: 0, severity: 'down', openedAt: rt.at || now, closedAt: null, text: `${slug} · worker stuck · ${t.stale} claim${t.stale > 1 ? 's' : ''} past heartbeat · ${t.queued} queued behind` });
    const oldest = Math.max(0, ...Object.values(rt.queues || {}).map((q) => q.oldestQueuedMs || 0));
    if (t.queued > 0 && oldest > 30 * 60e3 && !(t.stale > 0)) detected.push({ id: `${slug}:queue:backlog:${rt.at || now}`, slug, metric: 'queue', kind: 'backlog', value: t.queued, baseline: 0, z: 0, severity: 'warn', openedAt: (rt.at || now) - oldest, closedAt: null, text: `${slug} · backlog · ${t.queued} queued, oldest ${Math.round(oldest / 60e3)} min` });
  }
  const ids = new Set(detected.map((a) => a.id));
  const anomalies = [...detected, ...((src.anomalies || []).filter((a) => !ids.has(a.id)))];

  // 24h activity strip (req/hour, or tokens when there is no traffic).
  const hourly = new Array(24).fill(0); let anyReq = false;
  for (const s of Object.values(series)) for (let i = 0; i < s.ts.length; i += 1) { if (s.ts[i] < now - 86400e3) continue; const h = Math.floor((now - s.ts[i]) / 3600e3); if (h >= 0 && h < 24) { hourly[23 - h] += s.req[i] * (w.step / 60e3); if (s.req[i]) anyReq = true; } }
  if (!anyReq) for (const s of Object.values(series)) for (let i = 0; i < s.ts.length; i += 1) { if (s.ts[i] < now - 86400e3) continue; const h = Math.floor((now - s.ts[i]) / 3600e3); if (h >= 0 && h < 24) hourly[23 - h] += s.tokens[i]; }
  const peak = hourly.reduce((best, v, i) => (v > hourly[best] ? i : best), 0);

  const live = {}; const totals = { req: 0, s5xx: 0, p95: null, tokens: 0, active: 0 };
  let p95max = 0;
  for (const [slug, s] of Object.entries(series)) {
    const i = s.ts.length - 1;
    live[slug] = { req: s.req[i], s5xx: s.s5xx[i], p95: s.p95[i], tokens: s.tokens[i], convos: s.convos[i] };
    for (let k = 0; k < s.ts.length; k += 1) { totals.req += s.req[k] * (w.step / 60e3); totals.s5xx += s.s5xx[k] * (w.step / 60e3); totals.tokens += s.tokens[k]; p95max = Math.max(p95max, s.p95[k]); }
    if (s.req[i] > 0 || s.tokens[i] > 0) totals.active += 1;
  }
  totals.p95 = p95max || null;

  return {
    source: src.source || 'real',
    window: { ...w, n: Math.round(w.ms / w.step), now },
    series, flows, planeCalls, anomalies, live, totals,
    activity: { hourly, peakHour: anyReq || hourly.some(Boolean) ? peak : null, label: anyReq ? 'requests / hour · last 24h' : 'tokens / hour · last 24h' },
    usageDays: src.usageDays || {},
    runtime,
    scenario: src.scenario || null,
  };
}

export const fmtRate = (v) => (v == null ? '—' : v >= 1000 ? `${(v / 1000).toFixed(1)}k/min` : v >= 10 ? `${Math.round(v)}/min` : `${v.toFixed(1)}/min`);
